import React, { useEffect, useRef, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Search, X, AlertCircle, Filter, Info, CheckSquare, Square, Activity } from 'lucide-react';
import { useSearch } from '../hooks/useSearch';
import { useToggle } from '../hooks/useToggle';
import { useConsensusFilter } from '../hooks/useConsensusFilter';
import { highlightText } from '../utils/highlightText';
import SearchForm from '../components/SearchForm';
import LoadingSpinner from '../components/LoadingSpinner';
import NoResults from '../components/NoResults';
import ProteinLookupStatus from '../components/ProteinLookupStatus';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid'
import { Pagination, TabPagination } from '../components/Pagination';
import DownloadButton from '../components/DownloadButton';
import HowToCite from '../components/HowToCite';

const DiseaseSearch = () => {
    const {
        searchResults,
        setSearchResults,
        autocompleteResults,
        isLoading,
        setIsLoading, // Make sure this is included in destructuring
        searchTerm,
        setSearchTerm,
        showAutocomplete,
        autocompleteRef,
        inputRef,
        handleInputChange,
        handleAutocompleteClick,
        handleSearchSubmit,
        clearSearch,
        setShowAutocomplete,
        handleFullSearch,
        pagination,
        handlePageChange,
        handlePageSizeChange,
        tabData,
        tabLoading,
        tabErrors,
        fetchTabData,
        clearTabData,
        getTabData,
        isTabLoading,
        getTabError,
    } = useSearch('/api/disease/search', 'disease');

    const { expandedCards, expandedSections, toggleCard, toggleSection, expandSection } = useToggle();
    const [geneFilters, setGeneFilters] = React.useState({});
    const [highlightEnabled, setHighlightEnabled] = React.useState(false);
    const [geneHighlightEnabled, setGeneHighlightEnabled] = React.useState(false);
    const [showPaperModal, setShowPaperModal] = React.useState(false);
    const [selectedGenePapers, setSelectedGenePapers] = React.useState([]);
    const [selectedGeneName, setSelectedGeneName] = React.useState('');
    const modalRef = useRef(null);
    const [paperFilter, setPaperFilter] = React.useState('');
    const [paperHighlightEnabled, setPaperHighlightEnabled] = React.useState(false);
    const [geneDisplayMode, setGeneDisplayMode] = useState({}); // Replaced showOnlyConsensusGenes
    const [showDebugPanel, setShowDebugPanel] = useState(false);
    const [proteinLookupStatus, setProteinLookupStatus] = useState(null);
    const [activeTabs, setActiveTabs] = useState({});
    const [mutationFilters, setMutationFilters] = useState({});
    const [mutationHighlightEnabled, setMutationHighlightEnabled] = useState(false);
    const [paperFilters, setPaperFilters] = useState({});
    const [paperTabHighlightEnabled, setPaperTabHighlightEnabled] = useState(false);
    const [mutationDisplayMode, setMutationDisplayMode] = useState({});

    useEffect(() => {
        // When a disease card is expanded and the mutations tab is active
        if (expandedCards && activeTabs) {
            Object.keys(expandedCards).forEach(hdoid => {
                if (expandedCards[hdoid] && activeTabs[hdoid] === 'mutations') {
                    const disease = searchResults.find(d => d.hdoid === hdoid);

                    if (disease && mutationDisplayMode[hdoid] === 'consensus') {
                        // Update the pagination to reflect consensus count
                        const consensusCount = disease.consensus_stats?.total_consensus_mutations || 0;
                        const totalPages = Math.max(1, Math.ceil(consensusCount / (disease.mutations_pagination?.pageSize || 20)));

                        const updatedResults = searchResults.map(d => {
                            if (d.hdoid === hdoid) {
                                return {
                                    ...d,
                                    mutations_pagination: {
                                        ...d.mutations_pagination,
                                        totalFiltered: consensusCount,
                                        totalPages: totalPages,
                                        page: 1
                                    }
                                };
                            }
                            return d;
                        });
                        setSearchResults(updatedResults);
                    }
                }
            });
        }
    }, [expandedCards, activeTabs, mutationDisplayMode]);

    useEffect(() => {
        // When mutation display mode changes, make sure pagination shows correct counts
        Object.entries(mutationDisplayMode).forEach(([hdoid, mode]) => {
            if (mode === 'consensus') {
                const disease = searchResults.find(d => d.hdoid === hdoid);
                if (disease) {
                    const consensusCount = disease.consensus_stats?.total_consensus_mutations || 0;
                    const totalPages = Math.max(1, Math.ceil(consensusCount / (disease.mutations_pagination?.pageSize || 20)));

                    // Update pagination to reflect consensus count
                    setSearchResults(prevResults => prevResults.map(d => {
                        if (d.hdoid === hdoid) {
                            return {
                                ...d,
                                mutations_pagination: {
                                    ...d.mutations_pagination,
                                    totalFiltered: consensusCount,
                                    totalPages: totalPages
                                }
                            };
                        }
                        return d;
                    }));
                }
            } else if (mode === 'all') {
                const disease = searchResults.find(d => d.hdoid === hdoid);
                if (disease) {
                    const totalCount = disease.mutations_pagination?.total || 0;
                    const totalPages = Math.max(1, Math.ceil(totalCount / (disease.mutations_pagination?.pageSize || 20)));

                    // Update pagination to show all mutations
                    setSearchResults(prevResults => prevResults.map(d => {
                        if (d.hdoid === hdoid) {
                            return {
                                ...d,
                                mutations_pagination: {
                                    ...d.mutations_pagination,
                                    totalFiltered: totalCount,
                                    totalPages: totalPages
                                }
                            };
                        }
                        return d;
                    }));
                }
            }
        });
    }, [mutationDisplayMode, searchResults]);

    const debounce = (func, wait) => {
        let timeout;
        return function (...args) {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), wait);
        };
    };

    const diseaseConsensusFilter = (disease, consensusGenes, includesNormalized) => {
        if (!disease.genes || disease.genes.length === 0) return false;
        return disease.genes.some(gene => includesNormalized(consensusGenes, gene.humanEntrez));
    };

    const {
        consensusGenes,
        isLoadingConsensus,
        errorMessage,
        includesNormalized
    } = useConsensusFilter(searchResults, diseaseConsensusFilter);

    const fetchDiseaseGenesWithFilters = async (hdoid, filters = {}) => {
        if (!searchTerm) return;

        const disease = searchResults.find(d => d.hdoid === hdoid);
        const currentPage = filters.page || disease?.genes_pagination?.page || 1;
        const params = {
            hdoid: hdoid,
            page: currentPage,
            pageSize: filters.pageSize || disease?.genes_pagination?.pageSize || 20,
        };

        if (filters.term) params.term = filters.term;
        if (filters.mode && filters.mode !== 'all') {
            params.mode = filters.mode;
        }

        const data = await fetchTabData(hdoid, 'genes', '/api/disease/genes/filter', params);

        if (data && data.success) {
            // Get the original pagination data to preserve
            const originalTotal = disease?.genes_pagination?.total;
            const originalConsensusTotal = disease?.consensus_stats?.total_consensus_genes ||
                disease?.genes_pagination?.totalConsensus;

            const updatedResults = searchResults.map(disease => {
                if (disease.hdoid === hdoid) {
                    return {
                        ...disease,
                        genes_pagination: {
                            ...disease.genes_pagination,
                            ...data.pagination,
                            page: currentPage, // Preserve the current page
                            total: originalTotal || data.pagination.total, // Original total for pill count
                            totalFiltered: data.pagination.total, // Filtered total for pagination
                        },
                        consensus_stats: {
                            ...disease.consensus_stats,
                            total_consensus_genes: originalConsensusTotal ||
                                data.disease_stats?.total_consensus_genes ||
                                data.pagination.totalConsensus || 0
                        }
                    };
                }
                return disease;
            });

            setSearchResults(updatedResults);

            if (data.protein_lookup) {
                setProteinLookupStatus(data.protein_lookup);
            }
        }
    };

    const handleGenePageChange = (hdoid, newPage) => {
        fetchDiseaseGenesWithFilters(hdoid, {
            page: newPage,
            pageSize: searchResults.find(d => d.hdoid === hdoid)?.genes_pagination?.pageSize || 20,
            term: geneFilters[hdoid]?.term || '',
            mode: geneDisplayMode[hdoid] || 'all'
        });
    };

    const handleGenePageSizeChange = (hdoid, newPageSize) => {
        fetchDiseaseGenesWithFilters(hdoid, {
            page: 1, // Reset to page 1 when changing page size
            pageSize: newPageSize,
            term: geneFilters[hdoid]?.term || '',
            mode: geneDisplayMode[hdoid] || 'all'
        });
    };

    const handleMutationPageChange = (hdoid, newPage) => {
        // Get the current disease to preserve filter state
        const disease = searchResults.find(d => d.hdoid === hdoid);

        // Get the current filter term if one exists
        const currentFilterTerm = mutationFilters[hdoid]?.term || '';

        // Call fetchDiseaseMutationsWithFilters with preserved filter term
        fetchDiseaseMutationsWithFilters(hdoid, {
            page: newPage,
            pageSize: disease?.mutations_pagination?.pageSize || 20,
            term: currentFilterTerm // Preserve the current filter term
        });
    };

    const handleMutationPageSizeChange = (hdoid, newPageSize) => {
        // Get the current filter term if one exists
        const currentFilterTerm = mutationFilters[hdoid]?.term || '';

        fetchDiseaseMutationsWithFilters(hdoid, {
            page: 1, // Reset to page 1 when changing page size
            pageSize: newPageSize,
            term: currentFilterTerm // Preserve the current filter term
        });
    };

    const handlePaperPageChange = (hdoid, newPage) => {
        // Get the current disease to preserve filter state
        const disease = searchResults.find(d => d.hdoid === hdoid);

        // Get the current filter term if one exists
        const currentFilterTerm = paperFilters[hdoid]?.term || '';

        // Call fetchDiseasePapersWithFilters with preserved filter term
        fetchDiseasePapersWithFilters(hdoid, {
            page: newPage,
            pageSize: disease?.papers_pagination?.pageSize || 20,
            term: currentFilterTerm // Preserve the current filter term
        });
    };

    const handlePaperPageSizeChange = (hdoid, newPageSize) => {
        // Get the current filter term if one exists
        const currentFilterTerm = paperFilters[hdoid]?.term || '';

        fetchDiseasePapersWithFilters(hdoid, {
            page: 1, // Reset to page 1 when changing page size
            pageSize: newPageSize,
            term: currentFilterTerm // Preserve the current filter term
        });
    };

    const fetchDiseaseMutationsWithFilters = async (hdoid, filters = {}) => {
        if (!searchTerm) return;

        const params = {
            hdoid: hdoid,
            page: filters.page || 1,
            pageSize: filters.pageSize || 20
        };

        if (filters.term) {
            params.term = filters.term;
        }

        if (filters.mode && filters.mode !== 'all') {
            params.mode = filters.mode;
        }

        console.log("Fetching mutations with params:", new URLSearchParams(params).toString());
        const data = await fetchTabData(hdoid, 'mutations', '/api/disease/mutations/filter', params);
        console.log("Mutations API response:", data);

        if (data && data.success) {
            // Find the disease and update its mutations pagination data
            const updatedResults = searchResults.map(disease => {
                if (disease.hdoid === hdoid) {
                    // Get the original pagination and consensus data to preserve
                    const originalTotal = disease.mutations_pagination?.total || data.pagination.total;
                    const originalConsensusTotal = disease.consensus_stats?.total_consensus_mutations ||
                        data.pagination.totalConsensus;

                    return {
                        ...disease,
                        mutations_pagination: {
                            ...data.pagination,
                            total: originalTotal, // Preserve the original total count
                            totalFiltered: data.pagination.total, // Current filtered count
                        },
                        consensus_stats: {
                            ...disease.consensus_stats,
                            total_consensus_mutations: originalConsensusTotal,
                            mutation_consensus_percentage: disease.consensus_stats?.mutation_consensus_percentage ||
                                data.pagination.consensusPercentage || 0
                        }
                    };
                }
                return disease;
            });

            setSearchResults(updatedResults);
        }
    };
    const isConsensusMutation = (mutation) => {
        // If the mutation has an isConsensus property directly from the API, use that
        if (mutation.isConsensus !== undefined) {
            return mutation.isConsensus;
        }

        // Otherwise fall back to client-side checking using the humanEntrez property
        return includesNormalized(consensusGenes, mutation.humanEntrez);
    };

    // Add this new function for fetching papers with pagination
    const fetchDiseasePapersWithFilters = async (hdoid, filters = {}) => {
        if (!searchTerm) return;

        const params = {
            hdoid: hdoid,
            page: filters.page || 1,
            pageSize: filters.pageSize || 20
        };

        if (filters.term) {
            params.term = filters.term;
        }

        console.log("Fetching papers with params:", new URLSearchParams(params).toString());
        const data = await fetchTabData(hdoid, 'papers', '/api/disease/papers/filter', params);
        console.log("Papers API response:", data);

        if (data && data.success) {
            // Find the disease and update its papers pagination data
            const updatedResults = searchResults.map(disease => {
                if (disease.hdoid === hdoid) {
                    const updatedDisease = {
                        ...disease,
                        papers_pagination: data.pagination
                    };
                    console.log("Updated papers pagination for disease:", updatedDisease.papers_pagination);
                    return updatedDisease;
                }
                return disease;
            });

            setSearchResults(updatedResults);
        }
    };

    useEffect(() => {
        document.title = 'Disease Search';
        const handleClickOutside = (event) => {
            if (
                autocompleteRef.current &&
                !autocompleteRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setShowAutocomplete(false);
            }
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowPaperModal(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [autocompleteRef, inputRef, setShowAutocomplete]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.shiftKey && e.key === 'D') {
                setShowDebugPanel(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const openPaperModal = async (gene) => {
        setSelectedGeneName(gene.humanName);
        setShowPaperModal(true);
        setPaperFilter('');

        // Show loading state in the modal
        setSelectedGenePapers([{ isLoading: true }]);

        // If we already have papers data, use it
        if (gene.papers && gene.papers.length > 0) {
            console.log("Using existing papers data:", gene.papers.length);
            setSelectedGenePapers(gene.papers);
        }
        // Otherwise, if paperCount indicates there should be papers, fetch them
        else if (gene.paperCount > 0) {
            console.log("Fetching papers for gene:", gene.humanName, "ID:", gene.id);
            try {
                const papers = await fetchGenePapers(gene.id, gene.humanName);
                if (papers && papers.length > 0) {
                    setSelectedGenePapers(papers);
                } else {
                    setSelectedGenePapers([]);
                }
            } catch (error) {
                console.error("Failed to fetch papers:", error);
                setSelectedGenePapers([]);
            }
        } else {
            setSelectedGenePapers([]);
        }
    };

    // Alternative implementation using a dedicated API endpoint
    // You would need to create this endpoint in your backend
    const fetchGenePapers = async (geneId, geneName) => {
        try {
            // Try the main papers endpoint first
            let response = await fetch(`/api/gene/${geneId}/papers`);

            // If that fails, try the tab endpoint
            if (!response.ok) {
                console.log(`Primary endpoint failed, trying tab endpoint for gene ${geneId}`);
                response = await fetch(`/api/gene/${geneId}/tab/papers`);

                if (!response.ok) {
                    throw new Error(`Both API endpoints failed, status: ${response.status}`);
                }
            }

            const data = await response.json();

            if (!data.success || !data.data) {
                console.error("API returned success: false or no data");
                return [];
            }

            // Normalize data format from either endpoint
            // The tab endpoint returns more detailed paper objects with different field names
            return data.data.map(paper => {
                // Handle both endpoint response formats (direct papers and tab papers)
                return {
                    pmid: paper.pmid || paper.PMID,
                    year: paper.year || paper.Year,
                    name: paper.name || paper.Name || paper.PaperName || `Paper ${paper.pmid || paper.PMID}`,
                    paperTitle: paper.paperTitle || "No title available",
                    description: paper.description || paper.Description || paper.PaperDescription || "No description available"
                };
            });
        } catch (error) {
            console.error("Failed to fetch gene publications:", error);
            return [];
        }
    };
    const getDistinctGeneCount = (genes) => new Set(genes.map((gene) => gene.humanName)).size;
    const getConsensusGeneCount = (genes) => {
        if (!genes || !Array.isArray(genes)) return 0;
        return genes.filter(gene => isConsensusGene(gene)).length;
    };

    // Function to detect if a term might be a protein name
    const mightBeProteinName = (term) => {
        return /^[A-Za-z0-9\-]+$/.test(term) &&
            !term.match(/^[0-9]+$/) && // Not just a number
            term.length >= 3;
    };

    // Function to lookup protein names via API
    const lookupProteinName = async (name) => {
        try {
            setProteinLookupStatus({ loading: true, message: `Looking up protein "${name}"...` });
            const response = await fetch(`/api/protein/lookup?name=${encodeURIComponent(name)}`);
            const data = await response.json();

            if (data.success && data.data.length > 0) {
                setProteinLookupStatus({
                    success: true,
                    message: `Found ${data.data.length} genes related to protein "${name}": ${data.data.join(', ')}`
                });
                return data.data;
            } else {
                setProteinLookupStatus({
                    info: true,
                    message: `No genes found for protein "${name}". Continuing with regular filter.`
                });
                return [];
            }
        } catch (error) {
            console.error('Protein lookup failed:', error);
            setProteinLookupStatus({
                error: true,
                message: `Error looking up protein "${name}": ${error.message}`
            });
            return [];
        }
    };

    const handleGeneFilter = async (hdoid, filterTerm) => {
        setActiveTabs({ ...activeTabs, [hdoid]: 'genes' });

        // Clear protein lookup status if the filter is empty
        if (!filterTerm || filterTerm.trim() === '') {
            setProteinLookupStatus(null);
            setGeneFilters((prev) => ({
                ...prev,
                [hdoid]: null
            }));
        }
        // Check if the filter term might be a protein name
        else if (mightBeProteinName(filterTerm)) {
            // First attempt to look up the protein
            const geneSymbols = await lookupProteinName(filterTerm);

            if (geneSymbols && geneSymbols.length > 0) {
                // Store the filter with protein information
                setGeneFilters((prev) => ({
                    ...prev,
                    [hdoid]: {
                        term: filterTerm.toLowerCase(),
                        isProtein: true,
                        geneSymbols: geneSymbols,
                        geneSymbolsPattern: geneSymbols.join('|')
                    }
                }));

                // Show a success message
                setProteinLookupStatus({
                    success: true,
                    message: `Found ${geneSymbols.length} genes related to protein "${filterTerm}": ${geneSymbols.join(', ')}`
                });
            } else {
                // Store as a regular filter term if no protein matches
                setGeneFilters((prev) => ({
                    ...prev,
                    [hdoid]: {
                        term: filterTerm.toLowerCase(),
                        isProtein: false
                    }
                }));
            }
        } else {
            // Store as a regular filter term
            setGeneFilters((prev) => ({
                ...prev,
                [hdoid]: filterTerm ? {
                    term: filterTerm.toLowerCase(),
                    isProtein: false
                } : null
            }));
        }

        // Call backend with all current filters
        fetchDiseaseGenesWithFilters(hdoid, {
            page: 1, // Reset to first page when applying a new filter
            pageSize: searchResults.find(d => d.hdoid === hdoid)?.genes_pagination?.pageSize || 20,
            term: filterTerm || '', // Ensure we pass an empty string if filterTerm is null/undefined
            mode: geneDisplayMode[hdoid] || 'all'
        });
    };

    const handleGeneDisplayMode = (hdoid, mode) => {
        setActiveTabs({ ...activeTabs, [hdoid]: 'genes' });
        setGeneDisplayMode((prev) => ({ ...prev, [hdoid]: mode }));

        // Call backend with all current filters
        fetchDiseaseGenesWithFilters(hdoid, {
            page: 1, // Reset to first page when applying a new filter
            pageSize: searchResults.find(d => d.hdoid === hdoid)?.genes_pagination?.pageSize || 20,
            term: geneFilters[hdoid]?.term || '',
            mode: mode
        });
    };

    const handleMutationDisplayMode = (hdoid, mode) => {
        setActiveTabs({ ...activeTabs, [hdoid]: 'mutations' });
        setMutationDisplayMode((prev) => ({ ...prev, [hdoid]: mode }));

        // If switching back to 'all' mode, update pagination immediately
        if (mode === 'all') {
            const disease = searchResults.find(d => d.hdoid === hdoid);
            if (disease) {
                // Update pagination to show all mutations temporarily until API response
                const updatedResults = searchResults.map(d => {
                    if (d.hdoid === hdoid) {
                        const totalCount = disease.mutations_pagination?.total || 0;
                        const totalPages = Math.max(1, Math.ceil(totalCount / (disease.mutations_pagination?.pageSize || 20)));

                        return {
                            ...d,
                            mutations_pagination: {
                                ...d.mutations_pagination,
                                totalFiltered: totalCount,
                                totalPages: totalPages,
                                page: 1
                            }
                        };
                    }
                    return d;
                });
                setSearchResults(updatedResults);
            }
        }

        // Call backend with all current filters
        fetchDiseaseMutationsWithFilters(hdoid, {
            page: 1, // Reset to first page when applying a new filter
            pageSize: searchResults.find(d => d.hdoid === hdoid)?.mutations_pagination?.pageSize || 20,
            term: mutationFilters[hdoid]?.term || '',
            mode: mode
        });
    };

    const filterGenes = (genes, hdoid) => {
        if (!genes || !Array.isArray(genes)) return [];

        let filteredGenes = [...genes];

        const filterInfo = geneFilters[hdoid];
        if (filterInfo && filterInfo.term) {
            if (filterInfo.isProtein && filterInfo.geneSymbolsPattern) {
                // Use regex pattern of gene symbols generated from protein name lookup
                const regex = new RegExp(filterInfo.geneSymbolsPattern, 'i');
                filteredGenes = filteredGenes.filter((gene) =>
                    regex.test(gene.humanName.toLowerCase())
                );
            } else {
                // Traditional filter across multiple fields
                filteredGenes = filteredGenes.filter((gene) =>
                    [
                        gene.humanName,
                        gene.humanEntrez,
                        gene.mouseEntrez,
                        gene.ratEntrez,
                        gene.mgi,
                    ]
                        .map((value) => String(value || '').toLowerCase())
                        .some((value) => value.includes(filterInfo.term))
                );
            }
        }

        const displayMode = geneDisplayMode[hdoid] || 'all';
        if (displayMode === 'consensus') {
            filteredGenes = filteredGenes.filter(gene => isConsensusGene(gene));
        } else if (displayMode === 'non-consensus') {
            filteredGenes = filteredGenes.filter(gene => !isConsensusGene(gene));
        }

        return filteredGenes;
    };

    const isConsensusGene = (gene) => {
        // If the gene has an isConsensus property directly from the API, use that
        if (gene.isConsensus !== undefined) {
            return gene.isConsensus;
        }

        // If backend already tagged it, use that property
        if (gene.isConsensus !== undefined) {
            return gene.isConsensus;
        }

        // Otherwise fall back to client-side checking
        return includesNormalized(consensusGenes, gene.humanEntrez);
    };

    const filteredPapers = useMemo(() => {
        // Safety check for loading state or empty papers
        if (!selectedGenePapers || selectedGenePapers.some(p => p.isLoading)) {
            return [];
        }

        if (!paperFilter) return selectedGenePapers;

        const lowercaseFilter = paperFilter.toLowerCase();
        return selectedGenePapers.filter(paper => {
            // Make sure we safely check each property
            const pmidMatch = paper.pmid && paper.pmid.toString().includes(lowercaseFilter);
            const yearMatch = paper.year && paper.year.toString().includes(lowercaseFilter);
            const nameMatch = paper.name && paper.name.toLowerCase().includes(lowercaseFilter);
            const titleMatch = paper.paperTitle && paper.paperTitle.toLowerCase().includes(lowercaseFilter);
            const descMatch = paper.description && paper.description.toLowerCase().includes(lowercaseFilter);

            return pmidMatch || yearMatch || nameMatch || titleMatch || descMatch;
        });
    }, [selectedGenePapers, paperFilter]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const q = params.get('q');
        if (q) {
            setSearchTerm(q);
            // Optionally, update the input element's value too
            if (inputRef.current) {
                inputRef.current.value = q;
            }
            handleFullSearch(q);
        }
    }, [setSearchTerm, handleFullSearch, inputRef]);

    const setActiveTab = (hdoid, tabName) => {
        // Get the current active tab for this disease
        const currentTab = activeTabs[hdoid];
        const disease = searchResults.find(d => d.hdoid === hdoid);

        // Reset the filter and content for the tab we're leaving
        if (currentTab !== tabName) {
            if (currentTab === 'genes') {
                setGeneFilters((prev) => ({ ...prev, [hdoid]: null }));
                // Clear the gene filter input
                const geneInput = document.querySelector(`#gene-filter-${hdoid.replace(':', '-')}`);
                if (geneInput) geneInput.value = '';
            } else if (currentTab === 'mutations') {
                setMutationFilters((prev) => ({ ...prev, [hdoid]: null }));
                // Clear the mutations filter input
                const mutationInput = document.querySelector(`#mutation-filter-${hdoid.replace(':', '-')}`);
                if (mutationInput) mutationInput.value = '';
            } else if (currentTab === 'papers') {
                setPaperFilters((prev) => ({ ...prev, [hdoid]: null }));
                // Clear the papers filter input
                const paperInput = document.querySelector(`#paper-filter-${hdoid.replace(':', '-')}`);
                if (paperInput) paperInput.value = '';
            }
        }

        // Set the new active tab
        setActiveTabs({ ...activeTabs, [hdoid]: tabName });

        // If card is not expanded, expand it when a tab is clicked
        if (!expandedCards[hdoid]) {
            toggleCard(hdoid);
        }

        // Load data for the selected tab if not already loaded
        const tabKey = `${hdoid}-${tabName}`;
        if (!tabData[tabKey] && !tabLoading[tabKey]) {
            if (tabName === 'genes') {
                fetchDiseaseGenesWithFilters(hdoid, {
                    page: 1,
                    pageSize: disease?.genes_pagination?.pageSize || 20,
                    term: '',
                    mode: 'consensus' // Default to consensus view
                });
                // Update the gene display mode state
                setGeneDisplayMode(prev => ({ ...prev, [hdoid]: 'consensus' }));
            } else if (tabName === 'mutations') {
                // Set mutation display mode to consensus by default
                setMutationDisplayMode(prev => ({ ...prev, [hdoid]: 'consensus' }));
                fetchDiseaseMutationsWithFilters(hdoid, {
                    page: 1,
                    pageSize: 20,
                    mode: 'consensus'
                });
            } else if (tabName === 'papers') {
                fetchDiseasePapersWithFilters(hdoid, {
                    page: 1,
                    pageSize: 20
                });
            }
        }
    };

    const getGenesToDisplay = (hdoid) => {
        const key = `${hdoid}-genes`;
        return tabData[key] || [];
    };

    // Helper function to get mutations to display from tabData
    const getMutationsToDisplay = (hdoid) => {
        const key = `${hdoid}-mutations`;
        return tabData[key] || [];
    };

    // Helper function to get papers to display from tabData
    const getPapersToDisplay = (hdoid) => {
        const key = `${hdoid}-papers`;
        return tabData[key] || [];
    };

    const handleCardClick = (hdoid) => {
        // Toggle the card expanded state
        toggleCard(hdoid);

        // If the card is being expanded (was not expanded before), 
        // set default tab and fetch its data
        if (!expandedCards[hdoid]) {
            // Set genes as the default tab
            setActiveTabs(prev => ({ ...prev, [hdoid]: 'genes' }));

            // Get the disease object
            const disease = searchResults.find(d => d.hdoid === hdoid);

            // If the data for the genes tab hasn't been loaded yet, load it
            const tabKey = `${hdoid}-genes`;
            if (!tabData[tabKey] && !tabLoading[tabKey]) {
                // Default to consensus mode for genes
                setGeneDisplayMode(prev => ({ ...prev, [hdoid]: 'consensus' }));

                // Fetch genes data with consensus mode
                fetchDiseaseGenesWithFilters(hdoid, {
                    page: 1,
                    pageSize: disease?.genes_pagination?.pageSize || 20,
                    term: '',
                    mode: 'consensus'
                });
            }
        }
    };

    // Add these handler functions for mutations filter
    const handleMutationFilter = (hdoid, filterTerm) => {
        setActiveTabs({ ...activeTabs, [hdoid]: 'mutations' });

        // Store the filter in state for highlighting immediately
        setMutationFilters((prev) => ({
            ...prev,
            [hdoid]: filterTerm ? { term: filterTerm.toLowerCase() } : null
        }));

        // Use debounced function for the actual API call
        const debouncedFetch = debounce((term) => {
            fetchDiseaseMutationsWithFilters(hdoid, {
                page: 1, // Reset to first page when applying a new filter
                pageSize: searchResults.find(d => d.hdoid === hdoid)?.mutations_pagination?.pageSize || 20,
                term: term || '' // Ensure we pass an empty string if filterTerm is null/undefined
            });
        }, 300);

        debouncedFetch(filterTerm);
    };

    // Add these handler functions for papers filter
    const handlePaperFilter = (hdoid, filterTerm) => {
        setActiveTabs({ ...activeTabs, [hdoid]: 'papers' });

        // Store the filter in state for highlighting immediately
        setPaperFilters((prev) => ({
            ...prev,
            [hdoid]: filterTerm ? { term: filterTerm.toLowerCase() } : null
        }));

        // Use debounced function for the actual API call
        const debouncedFetch = debounce((term) => {
            fetchDiseasePapersWithFilters(hdoid, {
                page: 1, // Reset to first page when applying a new filter
                pageSize: searchResults.find(d => d.hdoid === hdoid)?.papers_pagination?.pageSize || 20,
                term: term || '' // Ensure we pass an empty string if filterTerm is null/undefined
            });
        }, 600);

        debouncedFetch(filterTerm);
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                    <Activity size={24} className="mr-2 text-blue-600" />
                    Disease Search
                </h1>
                <p className='mb-8 mt-2 text-gray-600'>Discover disease mechanisms and associated genetic disorders.</p>

                <SearchForm
                    searchTerm={searchTerm}
                    handleInputChange={handleInputChange}
                    handleSearchSubmit={handleSearchSubmit}
                    clearSearch={clearSearch}
                    autocompleteResults={autocompleteResults}
                    showAutocomplete={showAutocomplete}
                    handleAutocompleteClick={handleAutocompleteClick}
                    inputRef={inputRef}
                    autocompleteRef={autocompleteRef}
                    setShowAutocomplete={setShowAutocomplete}
                    highlightEnabled={highlightEnabled}
                    setHighlightEnabled={setHighlightEnabled}
                    placeholder="Search by disease name, HDOID, or description..."
                    highlightText={highlightText}
                />

                <p className='mt-1 text-xs text-gray-400 mb-8'>Accepted keywords [Human Disease Ontology Identifier (HDOID) , Disease Name/Description]</p>

                {errorMessage && (
                    <div className="mb-4 flex items-center text-red-600">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        <span className="text-sm">{errorMessage}</span>
                    </div>
                )}

                {showDebugPanel && (
                    <div className="mb-4 p-4 bg-gray-100 border border-gray-300 rounded">
                        <h3 className="font-medium mb-2">Debug Information</h3>
                        <div className="text-sm space-y-1">
                            <p>Consensus Genes Loaded: {consensusGenes.length}</p>
                            <p>Sample Consensus IDs: {consensusGenes.slice(0, 5).join(', ')}...</p>
                            <div className="mt-2 border-t border-gray-300 pt-2">
                                <p className="font-medium">Sample Disease Matches:</p>
                                <ul className="pl-5 list-disc">
                                    {searchResults.slice(0, 3).map((disease, idx) => {
                                        const matchingGenes = disease.genes?.filter(gene =>
                                            includesNormalized(consensusGenes, gene.humanEntrez)
                                        ) || [];
                                        return (
                                            <li key={idx} className={matchingGenes.length > 0 ? "text-green-600" : "text-red-600"}>
                                                {disease.description?.substring(0, 30)}...
                                                ({matchingGenes.length > 0 ?
                                                    `${matchingGenes.length} consensus genes` :
                                                    "No consensus genes"})
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {isLoading && <LoadingSpinner />}
                {!isLoading && pagination.totalPages > 1 && (
                    <Pagination
                        currentPage={pagination.page}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                        className="mb-6"
                    />
                )}

                <div className="space-y-6">
                    {searchResults.map((disease) => (
                        <div
                            key={disease.hdoid}
                            className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200"
                        >
                            <div
                                className="px-6 py-5 cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                                onClick={() => handleCardClick(disease.hdoid)}
                            >
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-xl font-medium text-gray-900">
                                            {highlightText(disease.description, searchTerm, highlightEnabled)}
                                        </h3>

                                        <div className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-4">
                                            <a
                                                href={`https://disease-ontology.org/?id=${disease.hdoid}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline flex items-center"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <span>Disease Ontology:</span>
                                                <span className="ml-1">{highlightText(disease.hdoid, searchTerm, highlightEnabled)}</span>
                                                <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                            </a>

                                            <a
                                                href={`https://www.alliancegenome.org/disease/${disease.hdoid}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline flex items-center"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <span>AllianceGenome.org</span>
                                                <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                            </a>
                                        </div>
                                    </div>

                                    <div className="flex items-center">
                                        <DownloadButton
                                            url={`/api/disease/${disease.hdoid}/csv`}
                                            label="Download Disease Data"
                                        />
                                        <div className="ml-2 p-1 rounded-full hover:bg-gray-200 transition-colors duration-150">
                                            {expandedCards[disease.hdoid] ? (
                                                <ChevronUp className="h-6 w-6 text-gray-600" />
                                            ) : (
                                                <ChevronDown className="h-6 w-6 text-gray-600" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="px-6">
                                <div className="bg-white">
                                    <nav className="flex -mb-px">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveTab(disease.hdoid, 'genes');
                                            }}
                                            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${(expandedCards[disease.hdoid] && (!activeTabs[disease.hdoid] || activeTabs[disease.hdoid] === 'genes'))
                                                ? 'border-blue-500 text-blue-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className="flex items-center">
                                                <span>Genes</span>
                                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                                                    Total {disease.genes_pagination?.total || 0}
                                                </span>
                                                {(disease.consensus_stats?.total_consensus_genes > 0 || disease.genes_pagination?.totalConsensus > 0) && (
                                                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                                                        Consensus {disease.consensus_stats?.total_consensus_genes || disease.genes_pagination?.totalConsensus}
                                                    </span>
                                                )}
                                            </div>
                                        </button>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveTab(disease.hdoid, 'mutations');
                                            }}
                                            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${(expandedCards[disease.hdoid] && activeTabs[disease.hdoid] === 'mutations')
                                                ? 'border-amber-500 text-amber-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            <div className="flex items-center">
                                                <span>Mutations</span>
                                                <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">
                                                    Total {disease.mutations_pagination?.total || 0}
                                                </span>
                                                {(disease.consensus_stats?.total_consensus_mutations > 0 || disease.mutations_pagination?.totalConsensus > 0) && (
                                                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                                                        Consensus {disease.consensus_stats?.total_consensus_mutations || disease.mutations_pagination?.totalConsensus}
                                                    </span>
                                                )}
                                            </div>
                                        </button>

                                        {disease.papers_pagination?.total > 0 && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveTab(disease.hdoid, 'papers');
                                                }}
                                                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${(expandedCards[disease.hdoid] && activeTabs[disease.hdoid] === 'papers')
                                                    ? 'border-indigo-500 text-indigo-600'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                    }`}
                                            >
                                                <div className="flex items-center">
                                                    <span>Synapse Proteomic Publications</span>
                                                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-800">
                                                        {disease.papers_pagination?.total || 0}
                                                    </span>
                                                </div>
                                            </button>
                                        )}
                                    </nav>
                                </div>
                            </div>

                            {expandedCards[disease.hdoid] && (
                                <div className="px-6">
                                    <div className="p-6">
                                        {/* Genes Tab */}
                                        {(!activeTabs[disease.hdoid] || activeTabs[disease.hdoid] === 'genes') && (
                                            <div>
                                                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        {consensusGenes.length > 0 && (
                                                            <div className="relative flex items-center w-48">
                                                                <Filter className="h-4 w-4 text-gray-400 mr-2" />
                                                                <select
                                                                    className="w-full pl-2 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                                    onChange={(e) => handleGeneDisplayMode(disease.hdoid, e.target.value)}
                                                                    value={geneDisplayMode[disease.hdoid] || 'all'}
                                                                >
                                                                    <option value="all">All Genes</option>
                                                                    <option value="consensus">Consensus Genes</option>
                                                                </select>
                                                            </div>
                                                        )}
                                                        <div className="relative flex items-center w-48">
                                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                            <input
                                                                id={`gene-filter-${disease.hdoid.replace(':', '-')}`}
                                                                type="text"
                                                                placeholder="Filter genes or proteins..."
                                                                className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                                onChange={(e) => handleGeneFilter(disease.hdoid, e.target.value)}
                                                            />
                                                            {geneFilters[disease.hdoid] && (
                                                                <button
                                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        const input = e.currentTarget.parentNode.querySelector('input');
                                                                        if (input) input.value = '';
                                                                        handleGeneFilter(disease.hdoid, '');
                                                                    }}
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => setGeneHighlightEnabled(!geneHighlightEnabled)}
                                                            className={`px-2 py-1 rounded-md text-xs font-medium ${geneHighlightEnabled
                                                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                                }`}
                                                        >
                                                            Highlight {geneHighlightEnabled ? 'On' : 'Off'}
                                                        </button>
                                                        <div className="relative group">
                                                            <button className="px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center">
                                                                <Info className="h-3.5 w-3.5 mr-1 text-gray-500" />
                                                                Tips
                                                            </button>
                                                            <div className="absolute left-0 transform -translate-y-full top-0 bg-gray-800 text-white text-xs rounded p-2 w-60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
                                                                <p className="mb-1">Enter a protein name to filter genes linked to that protein.</p>
                                                                <p>Example: Try "Camk2" to find calcium/calmodulin-dependent protein kinase genes.</p>
                                                                <div className="absolute h-2 w-2 bg-gray-800 transform rotate-45 left-4 -bottom-1"></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <DownloadButton
                                                        url={`/api/disease/${disease.hdoid}/allgenes/csv`}
                                                        label="Download Genes"
                                                    />
                                                </div>

                                                {tabLoading[`${disease.hdoid}-genes`] && <LoadingSpinner />}

                                                {tabErrors[`${disease.hdoid}-genes`] && (
                                                    <div className="mb-4 bg-red-50 text-red-700 px-4 py-2 rounded-md text-sm flex items-center">
                                                        <AlertCircle className="h-4 w-4 mr-2" />
                                                        <span>{tabErrors[`${disease.hdoid}-genes`]}</span>
                                                    </div>
                                                )}

                                                {disease.genes_pagination && disease.genes_pagination.totalPages > 1 && (
                                                    <TabPagination
                                                        pagination={disease.genes_pagination}
                                                        onPageChange={(newPage) => handleGenePageChange(disease.hdoid, newPage)}
                                                        onPageSizeChange={(newPageSize) => handleGenePageSizeChange(disease.hdoid, newPageSize)}
                                                        className="mb-4"
                                                    />
                                                )}

                                                {getGenesToDisplay(disease.hdoid).length > 0 ? (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                                        {getGenesToDisplay(disease.hdoid).map((gene, index) => (
                                                            <div key={index} className="bg-white p-4 rounded-md shadow-sm border border-gray-200">
                                                                <h5 className="font-medium text-gray-900 truncate flex items-center">
                                                                    <a href={`/gene-search?q=${encodeURIComponent(gene.humanName)}`} className="hover:text-blue-600 mr-2">
                                                                        <span className="italic">
                                                                            {highlightText(
                                                                                gene.humanName,
                                                                                geneFilters[disease.hdoid]?.term,
                                                                                geneHighlightEnabled
                                                                            )}
                                                                        </span>
                                                                    </a>
                                                                    {isConsensusGene(gene) && (
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                            Consensus
                                                                        </span>
                                                                    )}
                                                                </h5>
                                                                <div className="mt-3 text-sm">
                                                                    <table className="min-w-full">
                                                                        <tbody className="divide-y divide-gray-200">
                                                                            <tr>
                                                                                <td className="py-1 pr-2 text-gray-500 font-medium w-50">Human Entrez:</td>
                                                                                <td className="py-1">
                                                                                    <a
                                                                                        href={`https://www.ncbi.nlm.nih.gov/gene/${gene.humanEntrez}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="inline-block text-blue-600 hover:text-blue-600 no-underline"
                                                                                    >
                                                                                        {highlightText(gene.humanEntrez, geneFilters[disease.hdoid]?.term, geneHighlightEnabled)}
                                                                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                                                                    </a>
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="py-1 pr-2 text-gray-500 font-medium">Mouse Entrez:</td>
                                                                                <td className="py-1">
                                                                                    <a
                                                                                        href={`https://www.ncbi.nlm.nih.gov/gene/${gene.mouseEntrez}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="inline-block text-blue-600 hover:text-blue-600 no-underline"
                                                                                    >
                                                                                        {highlightText(gene.mouseEntrez, geneFilters[disease.hdoid]?.term, geneHighlightEnabled)}
                                                                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                                                                    </a>
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="py-1 pr-2 text-gray-500 font-medium">Rat Entrez:</td>
                                                                                <td className="py-1">
                                                                                    <a
                                                                                        href={`https://www.ncbi.nlm.nih.gov/gene/${gene.ratEntrez}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="inline-block text-blue-600 hover:text-blue-600 no-underline"
                                                                                    >
                                                                                        {highlightText(gene.ratEntrez, geneFilters[disease.hdoid]?.term, geneHighlightEnabled)}
                                                                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                                                                    </a>
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="py-1 pr-2 text-gray-500 font-medium">MGI:</td>
                                                                                <td className="py-1">
                                                                                    <a
                                                                                        href={`https://www.informatics.jax.org/marker/${gene.mgi}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                                                    >
                                                                                        {highlightText(gene.mgi, geneFilters[disease.hdoid]?.term, geneHighlightEnabled)}
                                                                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                                                                    </a>
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="py-1 pr-2 text-gray-500 font-medium">HPO:</td>
                                                                                <td className="py-1">
                                                                                    <a
                                                                                        href={`https://hpo.jax.org/browse/gene/NCBIGene:${gene.humanEntrez}`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                                                    >
                                                                                        {highlightText(`NCBIGene:${gene.humanEntrez}`, geneFilters[disease.hdoid]?.term, geneHighlightEnabled)}
                                                                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                                                                    </a>
                                                                                </td>
                                                                            </tr>
                                                                            <tr>
                                                                                <td className="py-1 pr-2 text-gray-500 font-medium">Proteomic Publications:</td>
                                                                                <td className="py-1">
                                                                                    <button
                                                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            openPaperModal(gene);
                                                                                        }}
                                                                                    >
                                                                                        {gene.paperCount} {gene.paperCount === 1 ? 'publication' : 'publications'}
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : !tabLoading[`${disease.hdoid}-genes`] && (
                                                    <p className="text-center py-8 text-gray-500">No genes associated with this disease.</p>
                                                )}

                                                {disease.genes_pagination && disease.genes_pagination.totalPages > 1 && (
                                                    <TabPagination
                                                        pagination={disease.genes_pagination}
                                                        onPageChange={(newPage) => handleGenePageChange(disease.hdoid, newPage)}
                                                        onPageSizeChange={(newPageSize) => handleGenePageSizeChange(disease.hdoid, newPageSize)}
                                                        className="mt-4"
                                                        showPageSize={false}
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {/* Mutations Tab */}
                                        {activeTabs[disease.hdoid] === 'mutations' && (
                                            <div>
                                                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        {consensusGenes.length > 0 && (
                                                            <div className="relative flex items-center w-48">
                                                                <Filter className="h-4 w-4 text-gray-400 mr-2" />
                                                                <select
                                                                    className="w-full pl-2 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
                                                                    onChange={(e) => handleMutationDisplayMode(disease.hdoid, e.target.value)}
                                                                    value={mutationDisplayMode[disease.hdoid] || 'all'}
                                                                >
                                                                    <option value="all">All Mutations</option>
                                                                    <option value="consensus">Consensus Gene Mutations</option>
                                                                </select>
                                                            </div>
                                                        )}
                                                        <div className="relative flex items-center w-48">
                                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                            <input
                                                                id={`mutation-filter-${disease.hdoid.replace(':', '-')}`}
                                                                type="text"
                                                                placeholder="Filter mutations..."
                                                                className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                                onChange={(e) => handleMutationFilter(disease.hdoid, e.target.value)}
                                                            />
                                                            {mutationFilters[disease.hdoid] && (
                                                                <button
                                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        const input = e.currentTarget.parentNode.querySelector('input');
                                                                        if (input) input.value = '';
                                                                        handleMutationFilter(disease.hdoid, '');
                                                                    }}
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => setMutationHighlightEnabled(!mutationHighlightEnabled)}
                                                            className={`px-2 py-1 rounded-md text-xs font-medium ${mutationHighlightEnabled
                                                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                                }`}
                                                        >
                                                            Highlight {mutationHighlightEnabled ? 'On' : 'Off'}
                                                        </button>
                                                    </div>
                                                    <DownloadButton
                                                        url={`/api/disease/${disease.hdoid}/allmutations/csv`}
                                                        label="Download Mutations"
                                                    />
                                                </div>

                                                {tabLoading[`${disease.hdoid}-mutations`] && <LoadingSpinner />}

                                                {tabErrors[`${disease.hdoid}-mutations`] && (
                                                    <div className="mb-4 bg-red-50 text-red-700 px-4 py-2 rounded-md text-sm flex items-center">
                                                        <AlertCircle className="h-4 w-4 mr-2" />
                                                        <span>{tabErrors[`${disease.hdoid}-mutations`]}</span>
                                                    </div>
                                                )}

                                                {disease.mutations_pagination && disease.mutations_pagination.totalPages > 1 && (
                                                    <TabPagination
                                                        pagination={disease.mutations_pagination}
                                                        onPageChange={(newPage) => handleMutationPageChange(disease.hdoid, newPage)}
                                                        onPageSizeChange={(newPageSize) => handleMutationPageSizeChange(disease.hdoid, newPageSize)}
                                                        className="mb-4"
                                                    />
                                                )}

                                                {getMutationsToDisplay(disease.hdoid).length > 0 ? (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                        {getMutationsToDisplay(disease.hdoid).map((mutation, index) => (
                                                            <div key={index} className="bg-gray-50 rounded-md p-4 shadow-sm">
                                                                <div className="mb-2">
                                                                    <h5 className="text-sm font-semibold text-gray-900 mb-1">
                                                                        {highlightText(
                                                                            mutation.variant,
                                                                            mutationFilters[disease.hdoid]?.term || '',
                                                                            mutationHighlightEnabled
                                                                        )}
                                                                    </h5>
                                                                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-700">
                                                                        <div>
                                                                            <span className="font-medium">Chromosome:</span>
                                                                            <span className="ml-1">
                                                                                {highlightText(
                                                                                    mutation.chromosome,
                                                                                    mutationFilters[disease.hdoid]?.term || '',
                                                                                    mutationHighlightEnabled
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Position:</span>
                                                                            <span className="ml-1">
                                                                                {highlightText(
                                                                                    mutation.position,
                                                                                    mutationFilters[disease.hdoid]?.term || '',
                                                                                    mutationHighlightEnabled
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="mt-2 space-y-1 text-xs text-gray-600">
                                                                    <div className="mb-1">
                                                                        <span className="font-medium">Gene:</span>
                                                                        <span className="ml-1">
                                                                            <a
                                                                                href={`/gene-search?q=${encodeURIComponent(mutation.geneName)}`}
                                                                                className="text-blue-600 hover:underline"
                                                                            >
                                                                                {highlightText(
                                                                                    mutation.geneName || 'N/A',
                                                                                    mutationFilters[disease.hdoid]?.term || '',
                                                                                    mutationHighlightEnabled
                                                                                )}
                                                                            </a>
                                                                        </span>
                                                                    </div>
                                                                    {mutation.humanEntrez && (
                                                                        <div className="mb-1">
                                                                            <span className="font-medium">Human Entrez:</span>
                                                                            <span className="ml-1">
                                                                                <a
                                                                                    href={`https://www.ncbi.nlm.nih.gov/gene/${mutation.humanEntrez}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="text-blue-600 hover:underline"
                                                                                >
                                                                                    {highlightText(
                                                                                        mutation.humanEntrez,
                                                                                        mutationFilters[disease.hdoid]?.term || '',
                                                                                        mutationHighlightEnabled
                                                                                    )}
                                                                                    <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                                                                </a>
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <div>
                                                                        <span className="font-medium">Function Class:</span>
                                                                        <span className="ml-1">
                                                                            {highlightText(
                                                                                mutation.functionClass || 'N/A',
                                                                                mutationFilters[disease.hdoid]?.term || '',
                                                                                mutationHighlightEnabled
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="font-medium">cDNA Variant:</span>
                                                                        <span className="ml-1">
                                                                            {highlightText(
                                                                                mutation.cdnaVariant || 'N/A',
                                                                                mutationFilters[disease.hdoid]?.term || '',
                                                                                mutationHighlightEnabled
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="font-medium">Protein Variant:</span>
                                                                        <span className="ml-1">
                                                                            {highlightText(
                                                                                mutation.proteinVariant || 'N/A',
                                                                                mutationFilters[disease.hdoid]?.term || '',
                                                                                mutationHighlightEnabled
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="font-medium">Exon/Intron:</span>
                                                                        <span className="ml-1">{mutation.exonIntron || 'N/A'}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="mt-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
                                                                    <div className="grid grid-cols-2 gap-2">
                                                                        <div>
                                                                            <span className="font-medium">DENOVO:</span>
                                                                            <span className="ml-1">{mutation.denovo ? 'Yes' : 'No'}</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">SFARI:</span>
                                                                            <span className="ml-1">{mutation.sfari ? 'Yes' : 'No'}</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Epilepsy Gene:</span>
                                                                            <span className="ml-1">{mutation.epilepsyGene ? 'Yes' : 'No'}</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">ClinVar:</span>
                                                                            <span className="ml-1">{mutation.clinVar || 'N/A'}</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Study Name:</span>
                                                                            <span className="ml-1">{mutation.studyName || 'N/A'}</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">PubMed:</span>
                                                                            {mutation.pubmedID ? (
                                                                                <span className="ml-1 space-x-1">
                                                                                    {mutation.pubmedID.split(';').map((id, index) => (
                                                                                        <a
                                                                                            key={index}
                                                                                            href={`https://pubmed.ncbi.nlm.nih.gov/${id.trim()}/`}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="text-blue-600 hover:underline mr-2"
                                                                                        >
                                                                                            {id.trim()}
                                                                                        </a>
                                                                                    ))}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="ml-1">N/A</span>
                                                                            )}
                                                                        </div>

                                                                    </div>
                                                                    {mutation.papers && mutation.papers.length > 0 && (
                                                                        <div className="mt-2">
                                                                            <span className="font-medium">Associated Publications:</span>
                                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                                {mutation.papers.map((pmid, idx) => (
                                                                                    <a
                                                                                        key={idx}
                                                                                        href={`https://pubmed.ncbi.nlm.nih.gov/${pmid}/`}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="text-blue-600 hover:underline"
                                                                                    >
                                                                                        {pmid}
                                                                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                                                                    </a>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : !tabLoading[`${disease.hdoid}-mutations`] && (
                                                    <div className="text-center py-8">
                                                        <p className="text-gray-500">No mutations associated with this disease.</p>
                                                    </div>
                                                )}

                                                {disease.mutations_pagination && disease.mutations_pagination.totalPages > 1 && (
                                                    <TabPagination
                                                        pagination={disease.mutations_pagination}
                                                        onPageChange={(newPage) => handleMutationPageChange(disease.hdoid, newPage)}
                                                        onPageSizeChange={(newPageSize) => handleMutationPageSizeChange(disease.hdoid, newPageSize)}
                                                        className="mt-4"
                                                        showPageSize={false}
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {/* Papers Tab */}
                                        {activeTabs[disease.hdoid] === 'papers' && (
                                            <div>
                                                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        <div className="relative flex items-center w-48">
                                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                            <input
                                                                id={`paper-filter-${disease.hdoid.replace(':', '-')}`}
                                                                type="text"
                                                                placeholder="Filter publications..."
                                                                className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                                onChange={(e) => handlePaperFilter(disease.hdoid, e.target.value)}
                                                            />
                                                            {paperFilters[disease.hdoid] && (
                                                                <button
                                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        const input = e.currentTarget.parentNode.querySelector('input');
                                                                        if (input) input.value = '';
                                                                        handlePaperFilter(disease.hdoid, '');
                                                                    }}
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => setPaperTabHighlightEnabled(!paperTabHighlightEnabled)}
                                                            className={`px-2 py-1 rounded-md text-xs font-medium ${paperTabHighlightEnabled
                                                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                                }`}
                                                        >
                                                            Highlight {paperTabHighlightEnabled ? 'On' : 'Off'}
                                                        </button>
                                                    </div>
                                                    <DownloadButton
                                                        url={`/api/disease/${disease.hdoid}/allpapers/csv`}
                                                        label="Download Publications"
                                                    />
                                                </div>



                                                {tabErrors[`${disease.hdoid}-papers`] && (
                                                    <div className="mb-4 bg-red-50 text-red-700 px-4 py-2 rounded-md text-sm flex items-center">
                                                        <AlertCircle className="h-4 w-4 mr-2" />
                                                        <span>{tabErrors[`${disease.hdoid}-papers`]}</span>
                                                    </div>
                                                )}

                                                {disease.papers_pagination && disease.papers_pagination.totalPages > 1 && (
                                                    <TabPagination
                                                        pagination={disease.papers_pagination}
                                                        onPageChange={(newPage) => handlePaperPageChange(disease.hdoid, newPage)}
                                                        onPageSizeChange={(newPageSize) => handlePaperPageSizeChange(disease.hdoid, newPageSize)}
                                                        className="mb-4"
                                                    />
                                                )}

                                                {tabLoading[`${disease.hdoid}-papers`] && <LoadingSpinner />}

                                                {getPapersToDisplay(disease.hdoid).length > 0 ? (
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                                        {getPapersToDisplay(disease.hdoid).map((paper, index) => (
                                                            <div
                                                                key={index}
                                                                className="bg-white p-4 rounded-md shadow-sm border border-gray-200"
                                                            >
                                                                <h5 className="font-medium text-gray-900 line-clamp-6 mb-2">
                                                                    <a
                                                                        href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}/`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-blue-600 hover:underline"
                                                                    >
                                                                        {highlightText(
                                                                            paper.paperTitle,
                                                                            paperFilters[disease.hdoid]?.term || '',
                                                                            paperTabHighlightEnabled
                                                                        )}
                                                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                                                    </a>
                                                                </h5>


                                                                <div className="flex justify-between text-sm">
                                                                    <a
                                                                        href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}/`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-blue-600 hover:underline"
                                                                    >
                                                                        PubMed ID: {highlightText(
                                                                            paper.pmid,
                                                                            paperFilters[disease.hdoid]?.term || '',
                                                                            paperTabHighlightEnabled
                                                                        )}
                                                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mb-3 inline" />
                                                                    </a>
                                                                    <span className="text-gray-500">
                                                                        {highlightText(
                                                                            paper.year,
                                                                            paperFilters[disease.hdoid]?.term || '',
                                                                            paperTabHighlightEnabled
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : !tabLoading[`${disease.hdoid}-papers`] && (
                                                    <div className="text-center py-8">
                                                        <p className="text-gray-500">No publications associated with this disease.</p>
                                                    </div>
                                                )}

                                                {disease.papers_pagination && disease.papers_pagination.totalPages > 1 && (
                                                    <TabPagination
                                                        pagination={disease.papers_pagination}
                                                        onPageChange={(newPage) => handlePaperPageChange(disease.hdoid, newPage)}
                                                        onPageSizeChange={(newPageSize) => handlePaperPageSizeChange(disease.hdoid, newPageSize)}
                                                        className="mt-4"
                                                        showPageSize={false}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {!isLoading && searchResults.length > 0 && pagination.totalPages > 1 && (
                    <Pagination
                        currentPage={pagination.page}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                        className="mt-6"
                    />
                )}

                {!isLoading && searchResults.length === 0 && <NoResults searchTerm={searchTerm} />}

                {/* Paper Modal */}
                {showPaperModal && (
                    <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center">
                        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
                        <div
                            ref={modalRef}
                            className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
                        >
                            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                                <h3 className="text-xl font-semibold text-gray-900">
                                    Publications for {selectedGeneName}
                                    {selectedGenePapers.length === 0 && <span className="text-sm font-normal text-gray-500 ml-2">
                                        (No publications found)
                                    </span>}
                                </h3>
                                <button
                                    onClick={() => setShowPaperModal(false)}
                                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                                >
                                    <X className="h-6 w-6" />
                                </button>
                            </div>
                            <div className="px-4 py-3 border-b border-gray-200">
                                <div className="flex items-center relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Filter publications by title, or PubMed ID..."
                                        className="pl-9 pr-3 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                        onChange={(e) => setPaperFilter(e.target.value)}
                                        value={paperFilter}
                                    />
                                    {paperFilter && (
                                        <button
                                            onClick={() => setPaperFilter('')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                        >
                                            <X className="h-4 w-4 text-gray-400 hover:text-gray-500" />
                                        </button>
                                    )}
                                </div>
                                <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                                    <span>
                                        {filteredPapers.length} of {selectedGenePapers.length} publications
                                    </span>
                                    <button
                                        onClick={() => setPaperHighlightEnabled(!paperHighlightEnabled)}
                                        className={`px-2 py-1 rounded-md text-xs font-medium ${paperHighlightEnabled
                                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Highlight {paperHighlightEnabled ? 'On' : 'Off'}
                                    </button>
                                </div>
                            </div>
                            <div className="p-6 overflow-y-auto flex-grow">
                                {selectedGenePapers.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-32 text-gray-500">
                                        <div className="mb-2">No publications available for this gene.</div>
                                    </div>
                                ) : selectedGenePapers.some(p => p.isLoading) ? (
                                    <div className="flex flex-col items-center justify-center h-32">
                                        <div className="mb-4">
                                            <LoadingSpinner size="medium" />
                                        </div>
                                        <div className="text-gray-600">Loading publications for {selectedGeneName}...</div>
                                    </div>
                                ) : filteredPapers.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {filteredPapers.map((paper, index) => (
                                            <div key={index} className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
                                                <h4 className="font-semibold text-gray-900 mb-3">
                                                    <a
                                                        href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}/`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 hover:underline flex items-start"
                                                    >
                                                        {highlightText(paper.paperTitle, paperFilter, paperHighlightEnabled)}
                                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500 ml-1 mt-1 flex-shrink-0" />
                                                    </a>
                                                </h4>

                                                <div className="flex justify-between items-center text-sm mt-4">
                                                    <a
                                                        href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}/`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 hover:underline flex items-center"
                                                    >
                                                        <span className="mr-1">PMID: {highlightText(paper.pmid, paperFilter, paperHighlightEnabled)}</span>
                                                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-blue-500" />
                                                    </a>
                                                    <span className="text-gray-500 font-medium">
                                                        {highlightText(paper.year ? paper.year.toString() : '', paperFilter, paperHighlightEnabled)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center text-gray-500">
                                        {selectedGenePapers.length > 0
                                            ? 'No publications match your filter criteria.'
                                            : 'No publications available for this gene.'}
                                    </p>
                                )}
                            </div>
                            <div className="border-t border-gray-200 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
                                    onClick={() => setShowPaperModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <section className="mt-24">
                    <HowToCite />
                </section>
            </div>
        </div>
    );
};

export default DiseaseSearch;